import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import type { MemoryDataPoint } from '../types';

interface Props {
  data: MemoryDataPoint[];
}

const deviceColors: Record<string, string> = {
  'Matt Flight Portal': '#00ff88',
  'Tess Flight Portal': '#00d4ff',
};

const tooltipStyle = {
  backgroundColor: '#0c1520',
  border: '1px solid #1a2a3a',
  borderRadius: '2px',
  fontFamily: 'Share Tech Mono, monospace',
  fontSize: '11px',
};

export function MemoryTrendsCard({ data }: Props) {
  const chartData = data.map(point => ({
    time: format(point.timestamp, 'HH:mm'),
    timestamp: point.timestamp.getTime(),
    [point.device]: (point.memFree / 1024).toFixed(1),
    device: point.device,
  }));

  const mergedData = chartData.reduce((acc, curr) => {
    const existing = acc.find(d => d.time === curr.time);
    if (existing) {
      Object.assign(existing, curr);
    } else {
      acc.push(curr);
    }
    return acc;
  }, [] as any[]);

  const devices = [...new Set(data.map(d => d.device))];

  return (
    <div className="hud-card">
      <div className="hud-corner-tl" /><div className="hud-corner-tr" /><div className="hud-corner-bl" /><div className="hud-corner-br" />
      <div className="hud-card-header">
        <div className="flex items-center gap-2.5">
          <svg className="w-4 h-4 text-hud-cyan opacity-80" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="4" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/>
            <line x1="5" y1="7" x2="5" y2="9" stroke="currentColor" strokeWidth="1"/>
            <line x1="8" y1="6" x2="8" y2="10" stroke="currentColor" strokeWidth="1"/>
            <line x1="11" y1="7.5" x2="11" y2="8.5" stroke="currentColor" strokeWidth="1"/>
          </svg>
          <span className="hud-card-title" style={{ color: '#00d4ff', textShadow: '0 0 8px rgba(0,212,255,0.15)' }}>Free Memory</span>
        </div>
        <span className="hud-card-badge" style={{ background: 'rgba(0,212,255,0.15)', color: '#00d4ff', borderColor: 'rgba(0,212,255,0.3)' }}>24H</span>
      </div>
      <div className="hud-card-body">
        <div className="h-48">
          {mergedData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mergedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#152030" />
                <XAxis
                  dataKey="time"
                  stroke="#1a2a3a"
                  tick={{ fill: '#3a5a6a', fontFamily: 'Share Tech Mono', fontSize: 10 }}
                />
                <YAxis
                  stroke="#1a2a3a"
                  tick={{ fill: '#3a5a6a', fontFamily: 'Share Tech Mono', fontSize: 10 }}
                  label={{ value: 'Free (KB)', angle: -90, position: 'insideLeft', fill: '#3a5a6a', fontFamily: 'Share Tech Mono', fontSize: 10 }}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend
                  wrapperStyle={{ fontFamily: 'Share Tech Mono', fontSize: '10px', color: '#5a7a8a' }}
                />
                {devices.map(device => (
                  <Line
                    key={device}
                    type="monotone"
                    dataKey={device}
                    stroke={deviceColors[device] || '#ffaa00'}
                    strokeWidth={1.5}
                    dot={false}
                    name={device}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="font-mono text-sm text-[#3a5a6a]">No memory data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
